/**
 * Local keyboard control.
 * Mappings from `SDLJeuInitN` / README.txt (2013), AZERTY layout:
 *   P1 ZQSD + A · P2 OKLM + I · P3 TFGH + R · P4 arrows + End
 *
 * Only one turn is kept per tick and per player (prevents U-turns via a double
 * press), and the bonus key fires on the rising edge.
 */

import type { PlayerInput } from "../sim/match";
import type { TurnInput } from "../sim/types";
import { Direction } from "../sim/types";

export interface KeyMap {
  up: string;
  down: string;
  left: string;
  right: string;
  bonus: string;
}

export const DEFAULT_KEYMAPS: readonly KeyMap[] = [
  { up: "z", down: "s", left: "q", right: "d", bonus: "a" },
  { up: "o", down: "l", left: "k", right: "m", bonus: "i" },
  { up: "t", down: "g", left: "f", right: "h", bonus: "r" },
  {
    up: "arrowup",
    down: "arrowdown",
    left: "arrowleft",
    right: "arrowright",
    bonus: "end",
  },
];

type Action = "up" | "down" | "left" | "right" | "bonus";

const TURN_OF: Record<"up" | "down" | "left" | "right", TurnInput> = {
  up: Direction.Up,
  down: Direction.Down,
  left: Direction.Left,
  right: Direction.Right,
};

export class KeyboardController {
  /** key (lowercase) -> list of (player, action). */
  private readonly bindings = new Map<string, Array<[number, Action]>>();
  private readonly queuedTurn: (TurnInput | null)[];
  private readonly bonusEdge: boolean[];
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleDown(e);

  /**
   * @param maps  one `KeyMap` per player (defaults to the 4 historical mappings)
   */
  constructor(maps: readonly KeyMap[] = DEFAULT_KEYMAPS) {
    this.queuedTurn = Array.from({ length: maps.length }, (): TurnInput | null => null);
    this.bonusEdge = Array.from({ length: maps.length }, () => false);
    maps.forEach((m, i) => {
      this.bind(m.up, i, "up");
      this.bind(m.down, i, "down");
      this.bind(m.left, i, "left");
      this.bind(m.right, i, "right");
      this.bind(m.bonus, i, "bonus");
    });
  }

  /** Adds a key (handy for also giving the arrows to P1 in solo). */
  bind(key: string, player: number, action: Action): void {
    const k = key.toLowerCase();
    const list = this.bindings.get(k) ?? [];
    list.push([player, action]);
    this.bindings.set(k, list);
  }

  attach(target: Window | HTMLElement = window): void {
    target.addEventListener("keydown", this.onKeyDown as EventListener);
  }

  detach(target: Window | HTMLElement = window): void {
    target.removeEventListener("keydown", this.onKeyDown as EventListener);
  }

  private handleDown(e: KeyboardEvent): void {
    const hits = this.bindings.get(e.key.toLowerCase());
    if (!hits) return;
    if (e.key.startsWith("Arrow")) e.preventDefault();
    for (const [player, action] of hits) {
      if (action === "bonus") {
        this.bonusEdge[player] = true;
      } else if (this.queuedTurn[player] === null) {
        this.queuedTurn[player] = TURN_OF[action];
      }
    }
  }

  /** Consumes the inputs accumulated since the last call. */
  poll(): PlayerInput[] {
    const out: PlayerInput[] = this.queuedTurn.map((turn, i) => ({
      turn,
      useBonus: this.bonusEdge[i] ?? false,
    }));
    this.queuedTurn.fill(null);
    this.bonusEdge.fill(false);
    return out;
  }
}
